import { combineReducers } from 'redux';

import {
  ADD_TO_CHECKOUT_REFERRER_HISTORY,
  CHECKOUT_CONFIGURE_IS_PROCESSING,
  CHECKOUT_DEFAULT_SELECTED_SHIP_OPTION,
  CHECKOUT_IS_LOADED,
  CHECKOUT_ORDER_BEING_PLACED,
  CHECKOUT_RESET_STATE,
  CHECKOUT_RESET_TO_DEFAULT_STATE,
  CHECKOUT_SET_CONFIRM_CARD_ERROR_MSG,
  CHECKOUT_SET_CONFIRMATION_PIXEL_PAYLOAD,
  CHECKOUT_SET_SHOULD_DEFAULT_OPTIONS,
  CHECKOUT_STORE_LOADING_REVIEW_SECTION,
  CHECKOUT_STORE_NO_PROMO_CODE_WARNING,
  CHECKOUT_TRACK_NEW_CUSTOMER,
  CHECKOUT_UPDATE_PURCHASE_CC,
  INITIALIZE_SAVED_ADDRESSES_AND_PAYMENTS_FETCHED,
  RECEIVE_CHECKOUT_DATA,
  RECEIVE_ORDER_INFORMATION,
  REMOVE_FROM_CHECKOUT_REFERRER_HISTORY,
  SET_CHECKOUT_MAX_AVAILABLE_STEP,
  SET_IS_ALSO_BILLING,
  SET_SHOULD_DEFAULT_OPTIONS,
  SET_USE_NAME_ON_ACCOUNT,
  TRACK_HAS_SEEN_IS_ALSO_BILLING,
  TRACK_HAS_TRACKED_PAGE_VIEW,
  UPDATE_CHECKOUT_FOR_TARGET_STEP,
  USE_PROMO_BALANCE
} from 'constants/reduxActions';
import {
  DIGITAL_GC_ONLY_CART,
  MIXED_WITH_BOTH_GC,
  MIXED_WITH_EGC,
  MIXED_WITH_PHYSICAL,
  NON_MIXED_WITH_BOTH_GC,
  PHYSICAL_GC_ONLY_CART,
  RETAIL_ONLY_CART
} from 'constants/cartTypes';
import {
  ADDRESS_STEP,
  CART_STEP,
  PAYMENT_STEP,
  REVIEW_STEP,
  SELECT_BILLING_ADDRESS_STEP,
  TBD_STEP
} from 'constants/checkoutFlow';
import { CONSTRAINT_VIOLATIONS_MESSAGES, GC_AND_PROMO_CONSTRAINTS, PROMOTIONAL_CODE_MISSING } from 'constants/constraintViolations';

import { addresses } from './addresses';
import { giftCards } from './giftCards';
import { giftOptions } from './giftOptions';
import { paymentTypes } from './payments';
import { shipOptions } from './shipOptions';

import {
  hasBadPromoCodeOrGiftCard,
  hasCartQuantityLimit,
  hasExpiredInstrument,
  hasGenericProblemWithPayment,
  hasInactiveInstrument,
  hasInvalidShippingOptions,
  hasProblemWithItemInCart,
  isMissingProducts,
  isShippableAddress,
  needsShippingOptions,
  needsToReAssociatePaymentToAddress,
  needsValidBillingAddress,
  needsValidPayment,
  needsValidShippingAddress
} from 'helpers/CheckoutUtils';
import { filter } from 'helpers/lodashReplacement';

const defaultState = {
  data: {},
  hasSeenIsAlsoBilling: false,
  isAlsoBilling: true,
  isFetchingCheckoutData: false,
  isLoaded: false,
  isLoading: false,
  maxAvailableStep: REVIEW_STEP,
  pageHistory: [],
  productsByLineItem: {},
  savedAddressAndPaymentsFetched: false,
  targetStep: TBD_STEP,
  useNameOnAccount: false,
  usePromoBalance: true,
  confirmCardMessage: null,
  isLoadingReviewSection: false,
  confirmationPixelPayload: null
};

export function purchaseData(state = defaultState, action = {}) {
  const {
    confirmCardMessage,
    confirmationPixelPayload,
    data,
    isAlsoBilling,
    isFetchingCheckoutData,
    isLoaded,
    isLoadingReviewSection,
    isNewCustomer,
    isOrderBeingPlaced,
    maxAvailableStep,
    page,
    purchaseCreditCard,
    purchaseShipOptionId,
    shouldDefaultOptions,
    showShouldDefaultOptions,
    targetStep,
    type,
    useNameOnAccount,
    usePromoBalance
  } = action;

  switch (type) {
    case RECEIVE_CHECKOUT_DATA: {
      const { purchaseStatus } = data;
      let newState = {
        ...state,
        data: purchaseStatus,
        isLoaded: true,
        isLoading: false,
        isFetchingCheckoutData: false,
        useNameOnAccount: false,
        confirmCardMessage: null,
        isLoadingReviewSection: false
      };

      // ********* Order is important ********
      newState = setCartType(newState);
      newState = storeConstraintViolations(newState);
      newState = storePaymentMethods(newState);
      newState = setDoesPurchaseRequireCreditCard(newState);
      newState = storeNumberOfItems(newState);
      newState = storeProductsByLineItem(newState);
      newState = setMaxAvailableStep(newState);

      return newState;
    }

    case CHECKOUT_SET_CONFIRM_CARD_ERROR_MSG: {
      return { ...state, confirmCardMessage };
    }

    case CHECKOUT_CONFIGURE_IS_PROCESSING: {
      return { ...state, isFetchingCheckoutData };
    }

    case INITIALIZE_SAVED_ADDRESSES_AND_PAYMENTS_FETCHED: {
      return { ...state, savedAddressAndPaymentsFetched: true };
    }

    case SET_SHOULD_DEFAULT_OPTIONS: {
      return { ...state, shouldDefaultOptions };
    }

    case TRACK_HAS_TRACKED_PAGE_VIEW: {
      return { ...state, hasTrackedPageView: true };
    }

    case RECEIVE_ORDER_INFORMATION: {
      return { ...state, orderInfo: data };
    }

    case SET_CHECKOUT_MAX_AVAILABLE_STEP: {
      return { ...state, maxAvailableStep };
    }

    case CHECKOUT_STORE_LOADING_REVIEW_SECTION: {
      return { ...state, isLoadingReviewSection };
    }

    case CHECKOUT_IS_LOADED: {
      return { ...state, isLoaded: isLoaded };
    }

    case SET_IS_ALSO_BILLING: {
      return { ...state, isAlsoBilling };
    }

    case CHECKOUT_SET_CONFIRMATION_PIXEL_PAYLOAD: {
      return { ...state, confirmationPixelPayload };
    }

    case CHECKOUT_ORDER_BEING_PLACED: {
      return { ...state, isOrderBeingPlaced };
    }

    case TRACK_HAS_SEEN_IS_ALSO_BILLING: {
      return { ...state, hasSeenIsAlsoBilling: true };
    }

    case REMOVE_FROM_CHECKOUT_REFERRER_HISTORY: {
      const newPageHistory = filter(state.pageHistory, item => item !== page) || [];
      return { ...state, pageHistory: newPageHistory };
    }

    case ADD_TO_CHECKOUT_REFERRER_HISTORY: {
      return { ...state, pageHistory: [...state.pageHistory, page] };
    }

    case CHECKOUT_SET_SHOULD_DEFAULT_OPTIONS: {
      return { ...state, showShouldDefaultOptions, shouldDefaultOptions };
    }

    case CHECKOUT_UPDATE_PURCHASE_CC: {
      let newState = { ...state, purchaseCreditCard };
      newState = storePurchaseBillingAddressId(newState);
      return newState;
    }

    case CHECKOUT_RESET_STATE: {
      return clearGiftCards(state);
    }

    case USE_PROMO_BALANCE: {
      return { ...state, usePromoBalance };
    }

    case CHECKOUT_DEFAULT_SELECTED_SHIP_OPTION: {
      return { ...state, purchaseShipOptionId };
    }

    case SET_USE_NAME_ON_ACCOUNT: {
      return { ...state, useNameOnAccount };
    }

    case UPDATE_CHECKOUT_FOR_TARGET_STEP: {
      return { ...state, targetStep };
    }

    case CHECKOUT_RESET_TO_DEFAULT_STATE: {
      return defaultState;
    }

    case CHECKOUT_STORE_NO_PROMO_CODE_WARNING: {
      const newState = clearGiftCards(state);
      const { constraintViolations } = newState;
      const { checkout: { giftCard: { [PROMOTIONAL_CODE_MISSING]: message } } } = CONSTRAINT_VIOLATIONS_MESSAGES;

      return {
        ...newState,
        constraintViolations: {
          ...constraintViolations,
          [PROMOTIONAL_CODE_MISSING]: { name: PROMOTIONAL_CODE_MISSING, message }
        }
      };
    }

    case CHECKOUT_TRACK_NEW_CUSTOMER: {
      return { ...state, isNewCustomer };
    }

    default:
      return state;
  }
}

export function storeProductsByLineItem(state) {
  const { data: { productList } } = state;

  if (!productList || !productList.length) {
    return state;
  }

  const productsByLineItem = productList.reduce((map, product) => {
    map[product.lineItemId] = product;
    return map;
  }, {});

  return { ...state, productsByLineItem };
}

export function storeNumberOfItems(state) {
  const { data: { productList } } = state;

  const numItems = productList.reduce((sum, item) => item.quantity + sum, 0);

  return { ...state, numItems };
}

function storePaymentMethods(state) {
  const { data: { paymentMethods } } = state;

  if (!paymentMethods) {
    return state;
  }

  const purchaseCreditCard = (paymentMethods || []).find(
    item => item.paymentMethodCode !== 'GC');

  const purchaseGiftCard = (paymentMethods || []).find(
    item => item.paymentMethodCode === 'GC');

  return { ...state, purchaseCreditCard, purchaseGiftCard };
}

export function storePurchaseBillingAddressId(state) {
  const { purchaseCreditCard } = state;
  const purchaseBillingAddressId = purchaseCreditCard && purchaseCreditCard.billingAddress ? purchaseCreditCard.billingAddress.addressId : null;
  return { ...state, purchaseBillingAddressId };
}

export function setDoesPurchaseRequireCreditCard(state) {
  const {
    cartType,
    data: {
      chargeSummary: { couponTotal, estimatedTax, grandTotal, totalBeforeTax },
      eligibleBalances: { couponBalance, gcBalance }
    }
  } = state;

  if (cartType !== RETAIL_ONLY_CART) {
    return { ...state, doesPurchaseRequireCC: true };
  }

  const arePromosApplied = couponTotal > 0;

  // promos are not yet applied
  // this isn't fool proof, but there is no fool proof option here
  // (coupon is pre tax, gc is post tax, so we'd have to recompute the tax which we can't do)
  const doesCouponBalanceCoverPurchase = couponBalance >= totalBeforeTax;
  const doesGiftCardBalanceCoverPurchase = gcBalance > 0 && gcBalance >= (totalBeforeTax - couponBalance + estimatedTax);
  const whenPromosNotApplied = !arePromosApplied && (doesCouponBalanceCoverPurchase || doesGiftCardBalanceCoverPurchase);

  // promos are applied
  const whenPromosApplied = arePromosApplied && grandTotal === 0;
  const doesPurchaseRequireCC = !(whenPromosNotApplied || whenPromosApplied);

  return { ...state, doesPurchaseRequireCC };
}

export function setCartType(state) {
  const { data: { productList = [] } } = state;
  const eGiftCards = filter(productList, item => item.gcCustomization !== null) || [];
  const physicalGiftCards = filter(productList, item => item.glProductGroupType === 'gl_gift_card' && item.gcCustomization === null) || [];
  const totalNumGiftCards = eGiftCards.length + physicalGiftCards.length;

  switch (true) {
    case eGiftCards.length === productList.length:
      return { ...state, cartType: DIGITAL_GC_ONLY_CART };
    case physicalGiftCards.length === productList.length:
      return { ...state, cartType: PHYSICAL_GC_ONLY_CART };
    case totalNumGiftCards === productList.length:
      return { ...state, cartType: NON_MIXED_WITH_BOTH_GC };
    case physicalGiftCards.length > 0 && eGiftCards.length > 0:
      return { ...state, cartType: MIXED_WITH_BOTH_GC };
    case physicalGiftCards.length > 0:
      return { ...state, cartType: MIXED_WITH_PHYSICAL };
    case eGiftCards.length > 0:
      return { ...state, cartType: MIXED_WITH_EGC };
    default:
      return { ...state, cartType: RETAIL_ONLY_CART };
  }
}

export function storeConstraintViolations(state) {
  const { data: { constraintViolations: cvs } } = state;
  const asinErrors = {};
  const constraintViolations = {};

  if (cvs) {
    cvs.forEach(cv => {
      constraintViolations[cv.name] = cv;
      if (cv.hasOwnProperty('asin')) {
        asinErrors[cv.asin] = asinErrors.hasOwnProperty(cv.asin)
          ? [...asinErrors[cv.asin], { ...cv }]
          : [{ ...cv }];
      }
    });
  }

  return { ...state, asinErrors, constraintViolations };
}

export function setMaxAvailableStep(state) {
  const {
    constraintViolations: cvs,
    data: { shippingAddress }
  } = state;

  const isAddressShippable = shippingAddress && shippingAddress.countryCode && isShippableAddress(shippingAddress.countryCode);
  const hasAddressAndItIsNotShippable = shippingAddress && !isAddressShippable;

  if (hasBadPromoCodeOrGiftCard(cvs)) {
    // change nothing, as could be adding GC from a few different places
    return state;
  }

  // ************************
  // Order here is important!
  // ************************
  switch (true) {
    case hasCartQuantityLimit(cvs):
    case isMissingProducts(cvs):
      // this means our middleware is broken, as should never get here
      return { ...state, maxAvailableStep: CART_STEP };
    case hasAddressAndItIsNotShippable:
    case needsValidShippingAddress(cvs):
      return { ...state, maxAvailableStep: ADDRESS_STEP };
    case needsValidPayment(cvs):
      return { ...state, maxAvailableStep: PAYMENT_STEP };
    case hasInactiveInstrument(cvs):
      return { ...state, maxAvailableStep: PAYMENT_STEP };
    case hasExpiredInstrument(cvs):
    case needsToReAssociatePaymentToAddress(cvs):
      return { ...state, maxAvailableStep: PAYMENT_STEP };
    case needsValidBillingAddress(cvs):
      return { ...state, maxAvailableStep: SELECT_BILLING_ADDRESS_STEP };
    case needsShippingOptions(cvs): {
      return { ...state, maxAvailableStep: REVIEW_STEP };
    }
    case hasInvalidShippingOptions(cvs):
      // can't kick to ship options step, as could be item that has no ship options
      return { ...state, maxAvailableStep: REVIEW_STEP };
    case hasGenericProblemWithPayment(cvs):
      return { ...state, maxAvailableStep: PAYMENT_STEP };
    case hasProblemWithItemInCart(cvs):
      return { ...state, maxAvailableStep: REVIEW_STEP };
    default:
      return { ...state, maxAvailableStep: REVIEW_STEP };
  }
}

export function clearGiftCards(state) {
  const { constraintViolations } = state;
  const withouthGiftCardViolations = {};
  Object.keys(constraintViolations).forEach(key => {
    if (!GC_AND_PROMO_CONSTRAINTS.includes(key)) {
      withouthGiftCardViolations[key] = constraintViolations[key];
    }
  });

  return { ...state, constraintViolations: withouthGiftCardViolations };
}

export default combineReducers({
  purchaseData,
  addresses,
  giftCards,
  giftOptions,
  paymentTypes,
  shipOptions
});
